/**
 * Takes a number and abbreviates it when it is large enough
 * @param {number} num number to be formatted
 * @param {number} fixedDecimal number of decimal places on un-abbreviated numbers
 * @param {number} fixedAbbr number of decimal places to show when number is abbreviated
 * @param {boolean} variableDecimal makes the fixedDecimal and fixed Abbr values dynamic so they won't show when round
 * @returns {string} Formatted number. Returns '-1' if a valid number was not provided
 */
export const abbreviateNumber = (num, fixedDecimal, fixedAbbr, variableDecimal) => {
    if (num === null || num === undefined) {
        return '-1';
    } // terminate early
    if (num === 0) {
        return '0';
    } // terminate early
    if (num < 0) {
        return '-1';
    } // terminate early

    let fixed = !fixedDecimal || fixedDecimal < 0 ? 0 : fixedDecimal;

    if (typeof fixedAbbr === 'number' && num >= 1000) {
        fixed = fixedAbbr;
    }

    let b = num.toPrecision(2).split('e'); // get power
    let k = b.length === 1 ? 0 : Math.floor(Math.min(b[1].slice(1), 14) / 3); // floor at decimals, ceiling at trillions
    let c = k < 1 ? num.toFixed(0 + fixed) : (num / Math.pow(10, k * 3)).toFixed(1 + fixed); // divide by power
    let d = c < 0 ? c : Math.abs(c); // enforce -0 is 0
    let e = d.toFixed(0 + fixed);
    if (variableDecimal) {
        e = Number(e); // drop insignificant decimal places
    }
    let f = e + ['', 'K', 'M', 'B', 'T'][k]; // append power

    return f;
};

// Add leading zeros to a number
export const padStart = (num, digits) => {
    let number = num.toString();
    if (number.length < digits) {
        for (let i = 0; i < digits - num.toString.length; i++) {
            number = '0' + number;
        }
    }
    return number;
};

// Format prices
export const formatPrice = (num, options) => {
    return (
        '$' +
        Number(num).toLocaleString(undefined, {
            minimumFractionDigits: options?.minimumFractionDigits ?? 2,
            maximumFractionDigits: options?.maximumFractionDigits ?? 2,
        })
    );
};

export const formatPriceFromCents = (num) => {
    return (
        '$' +
        Number(num / 100).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })
    );
};

// Format for calculations
export function formatNumber(num) {
    return Number(
        num
            .toFixed(2)
            .toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    );
}

// Format phone numbers
export const formatPhoneWithDashes = (phone) => {
    if (phone) {
        if (phone.length === 10) {
            return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        } else if (phone.length === 11) {
            return phone.replace(/(\d{1})(\d{3})(\d{3})(\d{4})/, '$1-$2-$3-$4');
        } else {
            return phone;
        }
    } else {
        return '';
    }
};

export const formatCurrencyInput = (value) => {
    let newValue = value;

    // Only one decimal point allowed
    const numArray = newValue.replace(/[^0-9.]/g, '').split('.');
    newValue = numArray.length > 1 ? numArray[0] + '.' + numArray[1] : numArray[0];

    // Max 2 decimal places
    if (numArray[1] && numArray[1].length > 2) {
        newValue = numArray[0] + '.' + numArray[1].slice(0, 2);
    }

    // Add 0 if there is nothing before the decimal point
    if (newValue.includes('.') && newValue.split('.')[0] === '') {
        newValue = '0' + newValue;
    }
    // Check if newValue is a number. Remove extra digits after decimal point
    if (!isNaN(newValue) || newValue === '') {
        if (newValue.includes('.') && newValue.split('.')[1].length > 2) {
            newValue = newValue.slice(0, newValue.indexOf('.') + 3);
        }
    }

    return newValue;
};

export const parseDecimalToPercent = (num, decimal = 0) => {
    return Number((num * 100).toFixed(decimal));
};

export const parsePercentToDecimal = (num, decimal = 0) => {
    return Number((num / 100).toFixed(decimal));
};
